import React, { useState, useMemo } from "react";
import { convertDistance, getUnitLabel } from '../utils/distance';
import { parseActivityDate } from '../utils/dateUtils';

const ActivityTable = ({ activities, showDateFilter = true, onActivityClick }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: 'start_date', direction: 'desc' });
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Filter activities by date range
  const filteredActivities = useMemo(() => {
    if (!showDateFilter || (!dateFilter.start && !dateFilter.end)) {
      return activities;
    }

    return activities.filter(activity => {
      const activityDate = parseActivityDate(activity);
      if (!activityDate) return false;

      const startDate = dateFilter.start ? new Date(dateFilter.start) : null;
      const endDate = dateFilter.end ? new Date(dateFilter.end) : null;

      if (startDate && activityDate < startDate) return false;
      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (activityDate > endOfDay) return false;
      }
      return true;
    });
  }, [activities, dateFilter, showDateFilter]);

  // Sort activities
  const sortedActivities = useMemo(() => {
    let sortableItems = [...filteredActivities];

    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'start_date':
            aValue = parseActivityDate(a)?.getTime() || 0;
            bValue = parseActivityDate(b)?.getTime() || 0;
            break;
          case 'name':
            aValue = (a.name || '').toLowerCase();
            bValue = (b.name || '').toLowerCase();
            break;
          case 'distance':
            aValue = a.distance || 0;
            bValue = b.distance || 0;
            break;
          case 'time':
            aValue = a.moving_time || a.elapsed_time || 0;
            bValue = b.moving_time || b.elapsed_time || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortableItems;
  }, [filteredActivities, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedActivities.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentActivities = sortedActivities.slice(startIndex, endIndex);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span style={{ color: '#ccc', marginLeft: '5px' }}>⇅</span>;
    }
    return sortConfig.direction === 'asc'
      ? <span style={{ color: '#14b8a6', marginLeft: '5px' }}>↑</span>
      : <span style={{ color: '#14b8a6', marginLeft: '5px' }}>↓</span>;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDateFilter({ start: '', end: '' });
    setCurrentPage(1);
  };

  return (
    <div>
      {showDateFilter && (
        <div style={{
          marginBottom: '20px',
          padding: '15px',
          background: '#f9fafb',
          borderRadius: '8px',
          display: 'flex',
          gap: '15px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <div>
            <label style={{ fontSize: '14px', color: '#6b7280', marginRight: '8px' }}>From:</label>
            <input
              type="date"
              value={dateFilter.start}
              onChange={(e) => setDateFilter({ ...dateFilter, start: e.target.value })}
              style={{
                padding: '8px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '14px'
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: '14px', color: '#6b7280', marginRight: '8px' }}>To:</label>
            <input
              type="date"
              value={dateFilter.end}
              onChange={(e) => setDateFilter({ ...dateFilter, end: e.target.value })}
              style={{
                padding: '8px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '14px'
              }}
            />
          </div>
          {(dateFilter.start || dateFilter.end) && (
            <button
              onClick={clearFilters}
              style={{
                padding: '8px 16px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Clear Filters
            </button>
          )}
          <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '14px' }}>
            Showing {sortedActivities.length} of {activities.length} activities
          </div>
        </div>
      )}

      <div style={{
        marginBottom: '15px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div>
          <label style={{ fontSize: '14px', color: '#6b7280', marginRight: '8px' }}>Show:</label>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '14px'
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: '8px' }}>per page</span>
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          Page {currentPage} of {totalPages || 1}
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th onClick={() => requestSort('start_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
              Date {getSortIndicator('start_date')}
            </th>
            <th onClick={() => requestSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
              Name {getSortIndicator('name')}
            </th>
            <th onClick={() => requestSort('distance')} style={{ cursor: 'pointer', userSelect: 'none' }}>
              Distance ({getUnitLabel()}) {getSortIndicator('distance')}
            </th>
            <th onClick={() => requestSort('time')} style={{ cursor: 'pointer', userSelect: 'none' }}>
              Time {getSortIndicator('time')}
            </th>
          </tr>
        </thead>
        <tbody>
          {currentActivities.length > 0 ? (
            currentActivities.map(activity => (
              <tr
                key={activity.id}
                onClick={() => onActivityClick && onActivityClick(activity)}
                style={{
                  cursor: onActivityClick ? 'pointer' : 'default',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (onActivityClick) e.currentTarget.style.background = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                <td>{formatDate(activity.start_date_local || activity.start_date)}</td>
                <td>{activity.name}</td>
                <td>{convertDistance(activity.distance)} {getUnitLabel()}</td>
                <td>{Math.floor((activity.moving_time || activity.elapsed_time || 0) / 60)} min</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                No activities found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div style={{
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'center',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              background: currentPage === 1 ? '#e5e7eb' : '#14b8a6',
              color: currentPage === 1 ? '#9ca3af' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            First
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              background: currentPage === 1 ? '#e5e7eb' : '#14b8a6',
              color: currentPage === 1 ? '#9ca3af' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Previous
          </button>

          {[...Array(Math.min(5, totalPages))].map((_, idx) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = idx + 1;
            } else if (currentPage <= 3) {
              pageNum = idx + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + idx;
            } else {
              pageNum = currentPage - 2 + idx;
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                style={{
                  padding: '8px 12px',
                  background: currentPage === pageNum ? '#14b8a6' : 'white',
                  color: currentPage === pageNum ? 'white' : '#14b8a6',
                  border: `1px solid ${currentPage === pageNum ? '#14b8a6' : '#d1d5db'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: currentPage === pageNum ? '600' : '400'
                }}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              background: currentPage === totalPages ? '#e5e7eb' : '#14b8a6',
              color: currentPage === totalPages ? '#9ca3af' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Next
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              background: currentPage === totalPages ? '#e5e7eb' : '#14b8a6',
              color: currentPage === totalPages ? '#9ca3af' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            Last
          </button>
        </div>
      )}

      <div style={{ marginTop: '15px', textAlign: 'center', fontSize: '14px', color: '#6b7280' }}>
        Showing {startIndex + 1} to {Math.min(endIndex, sortedActivities.length)} of {sortedActivities.length} activities
      </div>
    </div>
  );
};

export default ActivityTable;
